import React, { useState, useEffect } from 'react';
import { Bike, Play } from 'lucide-react';

interface EcycleRideProps {
  token: string;
  onUpdate: () => void;
}

interface RideStatus {
  hasActiveRide: boolean;
  ride?: {
    ride_id: string;
    progress: number;
    total_steps: number;
    started_at: string;
    last_updated: string;
    status: string;
  };
  canProgress: boolean;
  timeUntilNextStep?: number;
  canStartRide?: boolean;
  hydrogen_available?: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const EcycleRide: React.FC<EcycleRideProps> = ({ token, onUpdate }) => {
  const [rideStatus, setRideStatus] = useState<RideStatus | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [starting, setStarting] = useState(false);
  const [progressing, setProgressing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    checkRideStatus();
    const interval = setInterval(checkRideStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && rideStatus?.hasActiveRide && rideStatus.canProgress) {
      handleProgressRide();
    }
  }, [countdown]);

  const checkRideStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/ecycle/ride-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setRideStatus(data.data);
        if (data.data.hasActiveRide && data.data.timeUntilNextStep) {
          setCountdown(data.data.timeUntilNextStep);
        }
      }
    } catch (error) {
      console.error('Failed to check ride status:', error);
    }
  };

  const handleStartRide = async () => {
    setStarting(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/ecycle/start-ride`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        await checkRideStatus();
        onUpdate();
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: data.message });
        setTimeout(() => setMessage(null), 5000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to start ride' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setStarting(false);
    }
  };

  const handleProgressRide = async () => {
    setProgressing(true);

    try {
      const response = await fetch(`${API_URL}/ecycle/progress-ride`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        await checkRideStatus();
        onUpdate();
        setTimeout(() => setMessage(null), 5000);
      }
    } catch (error: any) {
      console.error('Failed to progress ride:', error);
    } finally {
      setProgressing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercent = rideStatus?.hasActiveRide 
    ? ((rideStatus.ride!.progress / rideStatus.ride!.total_steps) * 100)
    : 0;

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-3xl shadow-2xl p-8 border border-white/20">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-3 rounded-xl">
          <Bike className="text-white" size={28} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">E-Cycle Ride</h2>
          <p className="text-purple-200 text-sm">Farm → Market</p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-xl text-sm ${
          message.type === 'success' 
            ? 'bg-green-500/20 border border-green-400/50 text-green-100' 
            : 'bg-red-500/20 border border-red-400/50 text-red-100'
        }`}>
          <p className="font-semibold">{message.text}</p>
        </div>
      )}

      {!rideStatus?.hasActiveRide ? (
        /* No Active Ride - Show Start Button */
        <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl p-6 border border-cyan-400/30">
          <div className="mb-4">
            <p className="text-white text-sm mb-2">
              <strong>Journey:</strong> Transport goods from Farm to Market
            </p>
            <p className="text-white text-sm mb-2">
              <strong>Cost:</strong> 10 KG Hydrogen
            </p>
            <p className="text-white text-sm mb-2">
              <strong>Duration:</strong> 1 hour (12 steps × 5 min)
            </p>
            <p className="text-white text-sm mb-4">
              <strong>Reward:</strong> 1 KG Rice
            </p>
            {rideStatus?.canStartRide ? (
              <p className="text-green-300 text-sm">✅ You have enough Hydrogen to start!</p>
            ) : (
              <p className="text-red-300 text-sm">
                ⚠️ Need {(10 - (rideStatus?.hydrogen_available || 0)).toFixed(2)} more KG Hydrogen
              </p>
            )}
          </div>

          <button
            onClick={handleStartRide}
            disabled={!rideStatus?.canStartRide || starting}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition-all transform shadow-2xl flex items-center justify-center gap-3 ${
              rideStatus?.canStartRide && !starting
                ? 'bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 hover:from-cyan-500 hover:via-blue-600 hover:to-indigo-600 text-white hover:scale-[1.02]'
                : 'bg-gray-600/30 text-gray-400 cursor-not-allowed opacity-60'
            }`}
          >
            <Play size={24} className={starting ? 'animate-spin' : ''} />
            {starting ? 'Starting Ride...' : rideStatus?.canStartRide ? '🚴 Start Ride' : '⚠️ Not Enough H₂'}
          </button>
        </div>
      ) : (
        /* Active Ride - Show Progress */
        <div className="space-y-6">
          {/* Progress Bar with E-Cycle */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl p-6 border border-cyan-400/30">
            <div className="mb-4">
              <div className="flex justify-between text-sm text-white mb-2">
                <span>🏞️ Farm</span>
                <span className="font-bold">
                  Step {rideStatus.ride!.progress}/{rideStatus.ride!.total_steps}
                </span>
                <span>🏪 Market</span>
              </div>
              
              {/* Progress Track */}
              <div className="relative w-full bg-gray-700/50 rounded-full h-6 overflow-hidden mb-2">
                <div 
                  className="bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 h-full transition-all duration-1000 ease-linear rounded-full"
                  style={{ width: `${progressPercent}%` }}
                ></div>
                {/* E-Cycle Icon - FLIPPED to face right */}
                <div 
                  className="absolute top-0 h-full flex items-center transition-all duration-1000"
                  style={{ left: `calc(${progressPercent}% - 12px)` }}
                >
                  <div className="text-2xl transform scale-x-[-1]">🚴</div>
                </div>
              </div>

              <div className="text-center">
                {rideStatus.canProgress ? (
                  <button
                    onClick={handleProgressRide}
                    disabled={progressing}
                    className="text-green-300 text-sm font-semibold hover:text-green-200"
                  >
                    {progressing ? '⏳ Moving...' : '✅ Ready to progress!'}
                  </button>
                ) : (
                  <p className="text-purple-300 text-sm">
                    Next step in: <span className="font-mono font-bold text-cyan-300">{formatTime(countdown)}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Ride Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4">
              <p className="text-purple-300 text-xs mb-1">Progress</p>
              <p className="text-white font-bold text-lg">{progressPercent.toFixed(0)}%</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4">
              <p className="text-purple-300 text-xs mb-1">Time Remaining</p>
              <p className="text-white font-bold text-lg">
                {Math.ceil((rideStatus.ride!.total_steps - rideStatus.ride!.progress) * 5)} min
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EcycleRide;
